import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toValue,
  watch,
  type CSSProperties,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { TOUR_NAV_DRAWER_EVENT } from "@/composables/shared/useProductTour";
import { useSafeAreaInsets } from "@/composables/shared/useSafeAreaInsets";

const MOBILE_MAIN_BOTTOM_CHROME_PX = 56;
const NAVBAR_HEIGHT_COMPACT_PX = 48;
const NAVBAR_HEIGHT_FULL_PX = 78;
const VUETIFY_DEFAULT_TOOLBAR_HEIGHT_PX = 64;

type AppBarInstance = {
  $el?: Element;
};

type UseNavbarLayoutOptions = {
  fullscreen: MaybeRefOrGetter<boolean | undefined>;
  drawerOpen: Ref<boolean>;
  navBarShellRef: Readonly<Ref<HTMLElement | null>>;
  appBarRef: Readonly<Ref<AppBarInstance | null>>;
};

export function useNavbarLayout({
  fullscreen,
  drawerOpen,
  navBarShellRef,
  appBarRef,
}: UseNavbarLayoutOptions) {
  const route = useRoute();
  const { mobile, smAndDown, mdAndDown, mdAndUp, lgAndUp } = useDisplay();
  const safeAreaInsets = useSafeAreaInsets();

  const appBarHeight = ref(0);
  const isScrolled = ref(false);
  const isOverlayScrollBlocked = ref(false);

  let overlayObserver: MutationObserver | undefined;
  let resizeObserver: ResizeObserver | undefined;

  const compacted = computed(
    () => isScrolled.value || (Boolean(toValue(fullscreen)) && smAndDown.value)
  );

  const itemSize = computed(() => (toValue(fullscreen) || compacted.value ? 42 : 50));

  const mobileActionBtnColor = computed(() => (compacted.value ? "surface" : "background"));

  const appBarHeightProp = computed(() => {
    const baseHeight =
      compacted.value || toValue(fullscreen)
        ? VUETIFY_DEFAULT_TOOLBAR_HEIGHT_PX
        : NAVBAR_HEIGHT_FULL_PX;
    return baseHeight + safeAreaInsets.top.value;
  });

  const hasMobileBottomChromeInset = computed(() =>
    route.matched.some((record) => record.meta.mobileBottomChromeInset === true)
  );

  const mobileMainBottomInset = computed(() => {
    if (!mdAndDown.value) return 0;
    return hasMobileBottomChromeInset.value ? MOBILE_MAIN_BOTTOM_CHROME_PX : 0;
  });

  const effectiveAppBarHeight = computed(() => {
    if (appBarHeight.value > 0) return appBarHeight.value;
    return compacted.value ? NAVBAR_HEIGHT_COMPACT_PX : NAVBAR_HEIGHT_FULL_PX;
  });

  const drawerLayerStyle = computed(
    () =>
      ({
        "--nav-drawer-top": `${effectiveAppBarHeight.value}px`,
        "--nav-drawer-bottom": `${mobileMainBottomInset.value}px`,
      }) as CSSProperties
  );

  const updateAppBarHeight = () => {
    const shellHeight = navBarShellRef.value?.getBoundingClientRect().height ?? 0;
    const barElement = appBarRef.value?.$el;
    const barHeight =
      barElement instanceof HTMLElement ? barElement.getBoundingClientRect().height : 0;

    appBarHeight.value = Math.max(shellHeight, barHeight);
  };

  const syncAppBarHeight = () => {
    nextTick(() => {
      updateAppBarHeight();
      requestAnimationFrame(updateAppBarHeight);
    });
  };

  const onScroll = () => {
    if (isOverlayScrollBlocked.value || drawerOpen.value) return;
    isScrolled.value = window.scrollY > 0;
  };

  const onTourNavDrawer = (event: Event) => {
    const customEvent = event as CustomEvent<{ open: boolean }>;
    drawerOpen.value = customEvent.detail.open;
  };

  onMounted(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener(TOUR_NAV_DRAWER_EVENT, onTourNavDrawer);
    onScroll();

    const htmlElement = document.documentElement;
    const updateOverlayState = () => {
      isOverlayScrollBlocked.value = htmlElement.classList.contains("v-overlay-scroll-blocked");

      if (!isOverlayScrollBlocked.value) {
        onScroll();
      }
    };

    overlayObserver = new MutationObserver(updateOverlayState);
    overlayObserver.observe(htmlElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    updateOverlayState();

    nextTick(() => {
      updateAppBarHeight();

      if (typeof ResizeObserver === "undefined") return;

      resizeObserver = new ResizeObserver(updateAppBarHeight);

      if (navBarShellRef.value) {
        resizeObserver.observe(navBarShellRef.value);
      }

      const barElement = appBarRef.value?.$el;
      if (barElement instanceof HTMLElement) {
        resizeObserver.observe(barElement);
      }
    });
  });

  onBeforeUnmount(() => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener(TOUR_NAV_DRAWER_EVENT, onTourNavDrawer);
    overlayObserver?.disconnect();
    resizeObserver?.disconnect();
  });

  watch([compacted, mdAndDown, () => Boolean(toValue(fullscreen))], syncAppBarHeight);

  watch(drawerOpen, (open) => {
    if (open) syncAppBarHeight();
  });

  watch(mdAndDown, (isMobileLayout) => {
    if (!isMobileLayout) {
      drawerOpen.value = false;
    }
  });

  return {
    mobile,
    mdAndDown,
    mdAndUp,
    lgAndUp,
    isScrolled,
    compacted,
    itemSize,
    mobileActionBtnColor,
    drawerLayerStyle,
    effectiveAppBarHeight,
    appBarHeightProp,
  };
}
